import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Compass, LogOut, Moon, Settings as SettingsIcon, Sun } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { anzeigeName } from '../../lib/anzeigeName';
import { useThemeStore } from '../../stores/themeStore';
import { useUiStore } from '../../stores/uiStore';
import { useTourStore } from '../../stores/tourStore';
import { ViewAsDropdown } from '../ViewAsSwitcher';
import './UserMenu.css';

/**
 * Konto-Klappmenü oben in der Seitenleiste.
 *
 * Bündelt alles Persönliche an einer Stelle: Einstellungen, Erscheinungsbild,
 * die Einführungstour und das Abmelden. Vorher lagen diese Knöpfe als Reihe
 * kleiner Symbole ohne Beschriftung nebeneinander — was sie tun, musste man
 * raten oder ausprobieren.
 *
 * Offen/zu liegt im uiStore statt lokal, damit die Tour das Menü aufziehen
 * kann, um zu zeigen, was darin steckt.
 */
export function UserMenu() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { profile, role, logout } = useAuthStore();
  const { theme, toggle } = useThemeStore();
  const open = useUiStore((s) => s.userMenuOpen);
  const setOpen = useUiStore((s) => s.setUserMenuOpen);
  const startTour = useTourStore((s) => s.start);
  const tourRunning = useTourStore((s) => s.isRunning);
  const ref = useRef<HTMLDivElement>(null);

  const initials =
    `${profile?.first_name?.[0] ?? ''}${profile?.last_name?.[0] ?? ''}`.toUpperCase() || 'CC';
  const name = profile?.first_name || anzeigeName(profile?.first_name, profile?.last_name, profile?.email);

  // Klick daneben oder Escape schließt. Während der Tour nicht: dort steuert
  // die Tour, ob das Menü offen ist, und jeder Klick landet auf ihrer
  // Abdeckung — der würde das Menü sofort wieder zuklappen.
  useEffect(() => {
    if (!open || tourRunning) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, tourRunning, setOpen]);

  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <div className="cc-usermenu" ref={ref}>
      <button
        className="cc-usermenu__trigger"
        data-tour="usermenu"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="menu"
        title={profile?.email ?? 'Konto'}
      >
        <span className="cc-avatar">{initials}</span>
        <span className="cc-usermenu__who">
          <span className="cc-usermenu__name">{name}</span>
          <span className="cc-usermenu__role">{role ?? '—'}</span>
        </span>
        <ChevronDown size={15} className={`cc-usermenu__caret${open ? ' is-open' : ''}`} />
      </button>

      {open && (
        <div className="cc-usermenu__panel" data-tour="usermenu-panel" role="menu">
          <button className="cc-usermenu__item" role="menuitem" onClick={() => go('/settings')}>
            <SettingsIcon size={16} />
            <span>Einstellungen</span>
          </button>

          <button className="cc-usermenu__item" role="menuitem" onClick={toggle}>
            {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
            <span>{theme === 'light' ? 'Dunkles Erscheinungsbild' : 'Helles Erscheinungsbild'}</span>
          </button>

          <button
            className="cc-usermenu__item"
            role="menuitem"
            onClick={() => { setOpen(false); startTour(pathname); }}
          >
            <Compass size={16} />
            <span>Einführung neu starten</span>
          </button>

          {/* Admin-Vorschau „Ansicht als …" — rendert für alle anderen nichts */}
          <ViewAsSection />

          <div className="cc-usermenu__sep" />

          <button
            className="cc-usermenu__item cc-usermenu__item--danger"
            role="menuitem"
            onClick={() => { setOpen(false); void logout().then(() => navigate('/login')); }}
          >
            <LogOut size={16} />
            <span>Abmelden</span>
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Rollen-Vorschau für Admins. Eigener Abschnitt mit Überschrift, weil das
 * Auswahlfeld ohne Beschriftung im Menü sonst zusammenhanglos wirkt.
 */
function ViewAsSection() {
  const role = useAuthStore((s) => s.role);
  if (role !== 'admin') return null;
  return (
    <>
      <div className="cc-usermenu__sep" />
      <div className="cc-usermenu__section">
        <span className="cc-usermenu__label">Vorschau</span>
        <ViewAsDropdown />
      </div>
    </>
  );
}
